import {
  Mic, Brain, TrendingUp, Users, Zap, Shield,
  BarChart3, FileText, Globe, Target, Sparkles,
  CheckCircle2, ArrowRight, Briefcase, Calendar,
  Rocket, X
} from 'lucide-react';
import BackToHome from './BackToHome';

export default function Features() {
  return (
    <div className="bg-gradient-to-b from-white to-[#F0F4F3]">
      {/* Navigation Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <BackToHome to="/" />

            <div className="flex items-center gap-6">
              <a
                href="#features"
                className="text-[#6B7280] hover:text-[#1F2937] font-medium transition"
              >
                Features
              </a>
              <a
                href="#comparison"
                className="text-[#6B7280] hover:text-[#1F2937] font-medium transition"
              >
                vs 360°
              </a>
              <a
                href="/auth"
                className="bg-[#8CAFA8] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#7A9B94] transition"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Vision Statement */}
      <section className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#E0EBE9] text-[#7A9B94] px-4 py-2 rounded-full font-semibold text-sm mb-6">
            <Sparkles className="w-4 h-4" />
            The Future of Professional Growth
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1F2937] mb-6 leading-tight">
            Every Professional Deserves Better Feedback
          </h1>
          <p className="text-xl text-[#6B7280] mb-8 leading-relaxed">
            In five years, every professional will carry a Mentra Growth Profile —
            a living document of their strengths, growth trajectory, and authentic feedback
            from colleagues. No more lost insights, no more forgotten conversations.
            Just continuous, portable, AI-powered career growth.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-[#6B7280]">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>Voice-first feedback collection</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>AI-powered insights</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span>Portable growth profiles</span>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="container mx-auto px-6 py-20" id="features">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#1F2937] mb-4">
            Core Features That Transform Feedback
          </h2>
          <p className="text-xl text-[#6B7280] max-w-3xl mx-auto">
            Designed to make feedback collection effortless and insights actionable
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {/* Voice-First Feedback */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="bg-[#E0EBE9] w-14 h-14 rounded-[16px] flex items-center justify-center mb-6 group-hover:scale-110 transition">
              <Mic className="w-7 h-7 text-[#8CAFA8]" />
            </div>
            <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
              Voice-First Feedback Collection
            </h3>
            <p className="text-[#6B7280] mb-4 leading-relaxed">
              Colleagues share feedback by speaking for 3-5 minutes. No typing,
              no surveys, just authentic conversation. Voice captures nuance,
              tone, and depth that text simply can't match.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>10x faster than written surveys</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>More candid and detailed responses</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Automatic transcription included</span>
              </div>
            </div>
          </div>

          {/* AI-Powered Analysis */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="bg-green-100 w-14 h-14 rounded-[16px] flex items-center justify-center mb-6 group-hover:scale-110 transition">
              <Brain className="w-7 h-7 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
              AI-Powered Pattern Recognition
            </h3>
            <p className="text-[#6B7280] mb-4 leading-relaxed">
              Our advanced AI analyzes all feedback to identify recurring themes,
              hidden strengths, and development opportunities. See what everyone
              is saying — in aggregate and at a glance.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Identifies patterns across responses</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Surfaces blind spots automatically</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Generates personalized insights</span>
              </div>
            </div>
          </div>

          {/* Actionable Insights */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="bg-orange-100 w-14 h-14 rounded-[16px] flex items-center justify-center mb-6 group-hover:scale-110 transition">
              <Target className="w-7 h-7 text-orange-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
              Actionable Growth Recommendations
            </h3>
            <p className="text-[#6B7280] mb-4 leading-relaxed">
              Get specific, prioritized recommendations based on your feedback.
              No more wondering "what do I do with this?" — we tell you exactly
              where to focus your development energy.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Prioritized development areas</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Specific action steps</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Track progress over time</span>
              </div>
            </div>
          </div>

          {/* Anonymous Options */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="bg-purple-100 w-14 h-14 rounded-[16px] flex items-center justify-center mb-6 group-hover:scale-110 transition">
              <Shield className="w-7 h-7 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
              Anonymous & Confidential Feedback
            </h3>
            <p className="text-[#6B7280] mb-4 leading-relaxed">
              Enable anonymous feedback collection when you want the most honest
              responses. Perfect for sensitive topics, upward feedback, or
              360-degree reviews.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Toggle anonymous mode on/off</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Encrypted and secure storage</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>More candid feedback</span>
              </div>
            </div>
          </div>

          {/* Team Analytics */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="bg-indigo-100 w-14 h-14 rounded-[16px] flex items-center justify-center mb-6 group-hover:scale-110 transition">
              <BarChart3 className="w-7 h-7 text-indigo-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
              Team-Wide Analytics Dashboard
            </h3>
            <p className="text-[#6B7280] mb-4 leading-relaxed">
              Managers get aggregated insights across their entire team.
              Identify team strengths, culture gaps, and development needs
              without compromising individual privacy.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Team health metrics</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Culture and engagement insights</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Trend tracking over quarters</span>
              </div>
            </div>
          </div>

          {/* 360 Feedback Cycles */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="bg-pink-100 w-14 h-14 rounded-[16px] flex items-center justify-center mb-6 group-hover:scale-110 transition">
              <Users className="w-7 h-7 text-pink-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#1F2937] mb-3">
              360-Degree Feedback Cycles
            </h3>
            <p className="text-[#6B7280] mb-4 leading-relaxed">
              Run company-wide or team-wide feedback cycles with ease.
              Automated reminders, progress tracking, and beautiful summary
              reports for everyone.
            </p>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Schedule recurring cycles</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Automated reminders</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-[#6B7280]">
                <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>Progress tracking dashboard</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section className="container mx-auto px-6 py-20 bg-gradient-to-b from-[#F0F4F3] to-white">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#E0EBE9] text-[#7A9B94] px-4 py-2 rounded-full font-semibold text-sm mb-6">
            <Sparkles className="w-4 h-4" />
            Built for Scale
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#1F2937] mb-4">
            Advanced Capabilities for Growing Teams
          </h2>
          <p className="text-xl text-[#6B7280] max-w-3xl mx-auto leading-relaxed">
            From your first feedback request to enterprise-wide deployment, Mentra scales with you
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Portable Growth Profile */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="flex gap-4 mb-6">
              <div className="bg-[#E0EBE9] w-14 h-14 rounded-[16px] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                <Briefcase className="w-7 h-7 text-[#8CAFA8]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#1F2937] mb-2">
                  Portable Growth Profile
                </h3>
                <p className="text-sm text-[#8CAFA8] font-semibold">
                  Own your career data
                </p>
              </div>
            </div>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              Your feedback belongs to you forever, not to your employer. Export your complete growth profile — all feedback, insights, and development trends — whenever you transition to a new role or company.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">PDF Export</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">JSON Data</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Career Portfolio</span>
            </div>
          </div>

          {/* Integration Capabilities */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="flex gap-4 mb-6">
              <div className="bg-green-100 w-14 h-14 rounded-[16px] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                <Zap className="w-7 h-7 text-green-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#1F2937] mb-2">
                  Seamless Integrations
                </h3>
                <p className="text-sm text-green-600 font-semibold">
                  Works with your stack
                </p>
              </div>
            </div>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              Connect Mentra to the tools your team already uses daily. Send requests via Slack, sync with calendar apps, export to your HRIS, and authenticate through SSO. Zero disruption to existing workflows.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Slack</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">MS Teams</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">SSO/SAML</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Calendar</span>
            </div>
          </div>

          {/* Custom Templates */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="flex gap-4 mb-6">
              <div className="bg-orange-100 w-14 h-14 rounded-[16px] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                <FileText className="w-7 h-7 text-orange-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#1F2937] mb-2">
                  Custom Question Templates
                </h3>
                <p className="text-sm text-orange-600 font-semibold">
                  Tailored to your culture
                </p>
              </div>
            </div>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              Build reusable question sets aligned with your organization's competencies, values, and leadership framework. Create once, use everywhere — ensuring consistency while saving hours of setup time.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Saved Templates</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Team Library</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Role-Specific</span>
            </div>
          </div>

          {/* Export & Reporting */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="flex gap-4 mb-6">
              <div className="bg-purple-100 w-14 h-14 rounded-[16px] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                <TrendingUp className="w-7 h-7 text-purple-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#1F2937] mb-2">
                  Beautiful Reports & Analytics
                </h3>
                <p className="text-sm text-purple-600 font-semibold">
                  Share-ready insights
                </p>
              </div>
            </div>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              Generate professional PDF reports and visual dashboards perfect for performance reviews, promotion packages, or personal reflection. Transform raw feedback into presentation-ready insights in seconds.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">PDF Export</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Charts & Graphs</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Trend Analysis</span>
            </div>
          </div>

          {/* Global & Multilingual */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="flex gap-4 mb-6">
              <div className="bg-indigo-100 w-14 h-14 rounded-[16px] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                <Globe className="w-7 h-7 text-indigo-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#1F2937] mb-2">
                  Global & Multilingual
                </h3>
                <p className="text-sm text-indigo-600 font-semibold">
                  Truly borderless
                </p>
              </div>
            </div>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              Collect and analyze feedback in 25+ languages with automatic translation. Perfect for distributed teams, international workforces, and organizations with global operations. Everyone speaks, we understand.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">25+ Languages</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Auto Translation</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Cultural Context</span>
            </div>
          </div>

          {/* Enterprise Security */}
          <div className="bg-white p-8 rounded-[24px] shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_24px_rgba(0,0,0,0.06)] transition group">
            <div className="flex gap-4 mb-6">
              <div className="bg-red-100 w-14 h-14 rounded-[16px] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition">
                <Shield className="w-7 h-7 text-red-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-[#1F2937] mb-2">
                  Enterprise-Grade Security
                </h3>
                <p className="text-sm text-red-600 font-semibold">
                  Bank-level protection
                </p>
              </div>
            </div>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              Your data is protected by industry-leading security protocols. SOC 2 Type II certification, GDPR compliance, end-to-end encryption, and enterprise SSO/SAML. Trust is earned through action, not promises.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">SOC 2 Type II</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">GDPR</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">Encrypted</span>
              <span className="bg-[#F0F4F3] text-[#4B5563] text-xs px-3 py-1 rounded-full">SSO</span>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="max-w-4xl mx-auto mt-16 text-center bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] rounded-[24px] p-10 text-white">
          <h3 className="text-2xl md:text-3xl font-semibold mb-4">
            Need something more specific?
          </h3>
          <p className="text-lg text-white/90 mb-6 leading-relaxed">
            We're constantly adding new features based on customer needs. If you have a unique requirement or integration request, we'd love to hear from you.
          </p>
          <a
            href="/auth"
            className="inline-flex items-center gap-2 bg-white text-[#8CAFA8] px-6 py-3 rounded-lg font-semibold hover:bg-white/95 transition shadow-[0_2px_12px_rgba(0,0,0,0.1)]"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Mentra vs Traditional 360 Comparison */}
      <section className="container mx-auto px-6 py-20" id="comparison">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full font-semibold text-sm mb-6">
              <Zap className="w-4 h-4" />
              Why Teams Choose Mentra
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1F2937] mb-4">
              Mentra vs. Traditional 360° Feedback Cycles
            </h2>
            <p className="text-xl text-[#6B7280] max-w-3xl mx-auto">
              Traditional 360° feedback cycles are valuable but slow, heavy, and resource-intensive.
              Mentra eliminates that complexity and gives managers continuous, frictionless feedback
              in minutes—not months.
            </p>
          </div>

          {/* Comparison Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Traditional 360 Side */}
            <div className="bg-gray-100 rounded-[24px] p-8 border-2 border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gray-300 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#6B7280]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1F2937]">Traditional 360°</h3>
                  <p className="text-sm text-[#6B7280]">The old way</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">8-12+ weeks per cycle</div>
                    <p className="text-sm text-[#6B7280]">Planning, coordination, surveys, analysis, meetings</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Annual or semi-annual only</div>
                    <p className="text-sm text-[#6B7280]">Too resource-intensive for continuous feedback</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Heavy administrative burden</div>
                    <p className="text-sm text-[#6B7280]">Survey design, rater selection, reminders, scheduling</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Generic, flat data</div>
                    <p className="text-sm text-[#6B7280]">Rating scales and checkboxes lack depth and nuance</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Survey fatigue</div>
                    <p className="text-sm text-[#6B7280]">Long forms discourage honest, thoughtful responses</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Doesn't scale</div>
                    <p className="text-sm text-[#6B7280]">Every additional employee multiplies workload exponentially</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Lengthy reports sit unused</div>
                    <p className="text-sm text-[#6B7280]">Hard to translate into daily habits and action</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mentra Side */}
            <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-[24px] p-8 border-2 border-blue-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#8CAFA8] w-12 h-12 rounded-lg flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1F2937]">Mentra</h3>
                  <p className="text-sm text-[#6B7280]">The modern way</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Minutes, not months</div>
                    <p className="text-sm text-[#6B7280]">Send request, get insights immediately—no waiting</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Continuous feedback loops</div>
                    <p className="text-sm text-[#6B7280]">Get insights regularly, not just once or twice a year</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Zero administrative work</div>
                    <p className="text-sm text-[#6B7280]">Enter name, add message, click send—that's it</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Rich, human insights</div>
                    <p className="text-sm text-[#6B7280]">Voice and text capture authentic, detailed feedback</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">More honest responses</div>
                    <p className="text-sm text-[#6B7280]">Quick, conversational format with optional anonymity</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Scales effortlessly</div>
                    <p className="text-sm text-[#6B7280]">1 manager or 10,000—same simplicity, zero extra work</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-[#1F2937] mb-1">Immediate, actionable insights</div>
                    <p className="text-sm text-[#6B7280]">Clear themes and recommendations you can apply today</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-6 py-20 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-semibold text-[#1F2937] mb-6">
            Ready to Transform Your Professional Growth?
          </h2>
          <p className="text-xl text-[#6B7280] mb-10">
            Join thousands of professionals who are building better careers with AI-powered feedback.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/auth"
              className="bg-[#8CAFA8] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#7A9B94] transition inline-flex items-center gap-2 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#features"
              className="text-[#4B5563] hover:text-[#1F2937] font-semibold text-lg"
            >
              Learn more about features
            </a>
          </div>
          <p className="text-sm text-[#9CA3AF] mt-6">
            No credit card required • 5 feedback requests free per month • Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
}
